// src/pages/pedidos/CancelarPedido.js
import React, { useState, useEffect } from 'react'
import Modal from 'react-bootstrap/Modal'
import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import { toast } from 'react-toastify'

import { OPCIONES_MOTIVOS_CANCELACION } from 'config/pedidos'
import { formatNumberForDisplay } from 'utils/numberUtils'

function CancelarPedido(props) {
	const { pedido, cancelar, mostrar, confirmarCancelacion } = props

	const [motivoSeleccionado, setMotivoSeleccionado] = useState('')
	const [otroMotivo, setOtroMotivo] = useState('')
	const [loading, setLoading] = useState(false)

	// Resetear estados cuando se abra/cierre el modal
	useEffect(() => {
		if (mostrar) {
			setMotivoSeleccionado('')
			setOtroMotivo('')
		}
	}, [mostrar])

	const handleClose = () => {
		setMotivoSeleccionado('')
		setOtroMotivo('')
		cancelar()
	}

	const handleMotivoChange = (e) => {
		setMotivoSeleccionado(e.target.value)
		// Limpiar el motivo personalizado si se selecciona una opción predefinida
		if (e.target.value !== 'otro') {
			setOtroMotivo('')
		}
	}

	const handleConfirmar = async () => {
		// Validaciones
		if (!motivoSeleccionado) {
			toast.error('Debe seleccionar un motivo de cancelación')
			return
		}

		if (motivoSeleccionado === 'otro' && !otroMotivo.trim()) {
			toast.error('Debe especificar el motivo de cancelación')
			return
		}

		// Determinar el motivo final
		const motivoFinal = motivoSeleccionado === 'otro' ? otroMotivo.trim() : motivoSeleccionado

		try {
			setLoading(true)
			await confirmarCancelacion(pedido.id, motivoFinal)
		} catch (error) {
			console.error('Error al cancelar pedido:', error)
		} finally {
			setLoading(false)
		}
	}

	if (!pedido) return null

	return (
		<Modal show={mostrar} onHide={handleClose} backdrop="static" centered>
			<Modal.Header closeButton>
				<Modal.Title>
					<i className="fa fa-exclamation-triangle text-warning me-2"></i>
					Cancelar Pedido
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<Alert variant="warning">
					<strong>¿Está seguro que desea cancelar el pedido #{pedido.id}?</strong>
				</Alert>

				{/* Información básica del pedido */}
				<div className="mb-3 p-3 bg-light rounded">
					<div className="row">
						<div className="col-6">
							<small className="text-muted">Cliente:</small><br />
							<strong>
								{pedido.usuario ?
									`${pedido.usuario.nombre} ${pedido.usuario.apellido}` :
									(pedido.datosFacturacion?.razonSocial || 'No especificado')
								}
							</strong>
						</div>
						<div className="col-6">
							<small className="text-muted">Total:</small><br />
							<strong className="text-primary">
								₲ {formatNumberForDisplay(pedido.montoTotal) || '0'}
							</strong>
						</div>
					</div>
				</div>

				{/* Selección de motivo */}
				<Form.Group className="mb-3">
					<Form.Label>
						<strong>Motivo de la cancelación *</strong>
					</Form.Label>

					{/* Opciones predefinidas */}
					{OPCIONES_MOTIVOS_CANCELACION.map((opcion, index) => (
						<Form.Check
							key={index}
							type="radio"
							id={`motivo-${index}`}
							name="motivoCancelacion"
							value={opcion.label}
							label={opcion.label}
							checked={motivoSeleccionado === opcion.label}
							onChange={handleMotivoChange}
							className="mb-2"
						/>
					))}

					{/* Opción "Otro" */}
					<Form.Check
						type="radio"
						id="motivo-otro"
						name="motivoCancelacion"
						value="otro"
						label="Otro motivo (especificar)"
						checked={motivoSeleccionado === 'otro'}
						onChange={handleMotivoChange}
						className="mb-2"
					/>
				</Form.Group>

				{/* Campo de texto para motivo personalizado */}
				{motivoSeleccionado === 'otro' && (
					<Form.Group className="mb-3">
						<Form.Label>Especifique el motivo</Form.Label>
						<Form.Control
							as="textarea"
							rows={3}
							placeholder="Escriba el motivo de cancelación..."
							value={otroMotivo}
							onChange={(e) => setOtroMotivo(e.target.value)}
							maxLength={500}
						/>
						<Form.Text className="text-muted">
							Máximo 500 caracteres. Caracteres usados: {otroMotivo.length}/500
						</Form.Text>
					</Form.Group>
				)}

				<Alert variant="info" className="mb-0">
					<small>
						<i className="fa fa-info-circle me-1"></i>
						<strong>Obs:</strong> Esta acción no se puede deshacer. El pedido será marcado como cancelado
						y no podrá ser procesado ni entregado.
					</small>
				</Alert>
			</Modal.Body>

			<Modal.Footer>
				<Button
					variant="outline-secondary"
					onClick={handleClose}
					disabled={loading}
				>
					No cancelar
				</Button>
				<Button
					variant="danger"
					onClick={handleConfirmar}
					disabled={loading || !motivoSeleccionado || (motivoSeleccionado === 'otro' && !otroMotivo.trim())}
				>
					{loading ? (
						<>
							<i className="fa fa-spinner fa-spin me-2"></i>
							Cancelando...
						</>
					) : (
						<>
							<i className="fa fa-ban me-2"></i>
							Confirmar Cancelación
						</>
					)}
				</Button>
			</Modal.Footer>
		</Modal>
	)
}

export default CancelarPedido